import React, { useEffect, useState, useMemo } from "react";
import "../../Styles/invoiceTable.css";
import { toast } from "react-toastify";
import html2pdf from "html2pdf.js";
import TablePagination from "@mui/material/TablePagination";
import { BsSearch } from "react-icons/bs";
import ReLoginModal from "../../components/ReLoginModal";
import * as XLSX from "xlsx";
import { CircularProgress } from "@mui/material";
import api from "../../util/axios";

// Define more specific types for LoadingSlip
interface Requisition {
  approvalNumber: string;
  customer?: { businessName: string };
  itemName: string;
  unitPrice: number;
  quantity: number;
  quantityToLoad: number;
  totalCost: number;
  quantityLoaded: number;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
}

interface LoadingSlip {
  loadingNumber: string;
  createdAt: string;
  truckNumber: string;
  requisitions: Requisition[];
  invoice: Invoice | null;
  _id: string;
}

const LoadingReportTable = () => {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [reLogin, setReLogin] = useState(false);
  const [getLoadingSlip, setGetLoadingSlip] = useState<LoadingSlip[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null); // Added error state
  const [searchQuery, setSearchQuery] = useState("");
  const [productQuery, setProductQuery] = useState("");
  const [businessQuery, setBusinessQuery] = useState("");
  const [truckQuery, setTruckQuery] = useState("");
  const [searchField, setSearchField] = useState("businessName");

  const generatePDF = () => {
    const slipElement = document.querySelector(".invoiceTable");
    if (slipElement) {
      const pdfOptions = {
        margin: 2,
        filename: "LoadingSlip-Report.pdf",
        image: { type: "png" },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
      };
      html2pdf().from(slipElement).set(pdfOptions).save();
    }
  };

  const exportToExcel = () => {
    const filteredData = filteredLoadingSlips?.map((slip, inx) => {
      const dateString = slip.createdAt;
      const formattedDate = new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });

      return {
        "S/N": inx + 1,
        "Business Name": slip.requisitions[0]?.customer?.businessName ?? "N/A",
        "Item Name": slip.requisitions[0]?.itemName ?? "N/A",
        "Unit Price": slip.requisitions[0]?.unitPrice ?? 0,
        "Quantity to Load": slip.requisitions[0]?.quantityToLoad ?? 0,
        "Truck Number": slip.truckNumber ?? "N/A",
        "Loading No": slip.loadingNumber ?? "N/A",
        "Invoice No": slip.invoice?.invoiceNumber ?? "N/A",
        "Total Cost": slip.requisitions[0]?.totalCost ?? 0,
        Date: formattedDate,
      };
    }) ?? [];

    const ws = XLSX.utils.json_to_sheet(filteredData);
    ws['!cols'] = [
      { width: 5 },
      { width: 25 },
      { width: 20 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 20 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "LoadingReports");
    XLSX.writeFile(wb, "Loading-Report.xlsx");
  };

  const calculateTotalQuantityToLoad = (data: LoadingSlip[]) => {
    return data.reduce((total, req) => {
      return total + (req.requisitions[0]?.quantityToLoad || 0);
    }, 0);
  };

  const calculateCustomerQuantityToLoad = (data: LoadingSlip[]) => {
    return data.reduce((acc, req) => {
      const customerName = req.requisitions[0]?.customer?.businessName || "Unknown";
      acc[customerName] = (acc[customerName] || 0) + (req.requisitions[0]?.quantityToLoad || 0);
      return acc;
    }, {} as Record<string, number>);
  };

  const token = window.localStorage.getItem("token");
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  function queryRequisition() {
    setIsLoading(true);
    setError(null); // Reset error state
    return api
      .get("/loading-slip", { headers })
      .then((res) => {
        setGetLoadingSlip(res.data.data);
        // console.log("Loading slips fetched:", res.data.data);
      })
      .catch((error) => {
        if (
          error.response?.data?.message === "jwt expired" ||
          error.response?.data?.message === "jwt malformed"
        ) {
          toast.error('Session expired, please log in again.');
          setReLogin(true);
        } else {
          setError("Failed to fetch loading slips. Please try again later.");
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  useEffect(() => {
    queryRequisition();
  }, []);

  const filteredLoadingSlips = useMemo(() => {
    return getLoadingSlip.filter((slip) => {
      const slipDate = new Date(slip.createdAt);
      const startDate = fromDate ? new Date(fromDate) : null;
      const endDate = toDate ? new Date(toDate) : null;

      if (startDate && slipDate < startDate) return false;
      if (endDate && slipDate > endDate) return false;

      if (searchField === "combined") {
        if (!businessQuery && !productQuery && !truckQuery) return true;
      } else if (!searchQuery) {
        return true;
      }

      const customerName = slip.requisitions[0]?.customer?.businessName?.toLowerCase() || "";
      const productName = slip.requisitions[0]?.itemName?.toLowerCase() || "";
      const truckNumber = slip.truckNumber?.toLowerCase() || "";

      const businessNameMatch = customerName.includes(
        searchField === "combined" ? businessQuery.toLowerCase() : searchQuery.toLowerCase()
      );
      const productNameMatch = productName.includes(
        searchField === "combined" ? productQuery.toLowerCase() : searchQuery.toLowerCase()
      );
      const truckNumberMatch = truckNumber.includes(
        searchField === "combined" ? truckQuery.toLowerCase() : searchQuery.toLowerCase()
      );

      switch (searchField) {
        case "businessName":
          return businessNameMatch;
        case "productName":
          return productNameMatch;
        case "truckNumber":
          return truckNumberMatch;
        case "combined":
          return businessNameMatch && productNameMatch && truckNumberMatch;
        default:
          return businessNameMatch;
      }
    });
  }, [getLoadingSlip, fromDate, toDate, searchQuery, productQuery, businessQuery, truckQuery, searchField]);

  const paginatedLoadingSlips = filteredLoadingSlips.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const totalQuantityToLoad = calculateTotalQuantityToLoad(paginatedLoadingSlips);
  const customerQuantities = calculateCustomerQuantityToLoad(paginatedLoadingSlips);

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <div className="board">
      {reLogin && <ReLoginModal />}
      <div className="method">
        <div className="searchFieldSelect">
          <label htmlFor="filter">Search filter: </label>
          <select
            id="filter"
            value={searchField}
            onChange={(e) => setSearchField(e.target.value)}
          >
            <option value="businessName">Customer Name</option>
            <option value="productName">Product Name</option>
            <option value="truckNumber">Truck Number</option>
            <option value="combined">Mixed search</option>
          </select>
        </div>
        {searchField === "combined" ? (
          <div className="combinedSearch">
            <label htmlFor="businessQuery" className="sr-only">Customer Name</label>
            <input
              id="businessQuery"
              type="text"
              placeholder="Customer Name"
              value={businessQuery}
              onChange={(e) => setBusinessQuery(e.target.value)}
            />
            <label htmlFor="productQuery" className="sr-only">Product Name</label>
            <input
              id="productQuery"
              type="text"
              placeholder="Product Name"
              value={productQuery}
              onChange={(e) => setProductQuery(e.target.value)}
            />
            <label htmlFor="truckQuery" className="sr-only">Truck Number</label>
            <input
              id="truckQuery"
              type="text"
              placeholder="Truck Number"
              value={truckQuery}
              onChange={(e) => setTruckQuery(e.target.value)}
            />
          </div>
        ) : (
          <div className="apInput">
            <label htmlFor="searchQuery" className="sr-only">Search</label>
            <input
              id="searchQuery"
              type="text"
              value={searchQuery}
              placeholder="Search"
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <BsSearch className="apIcon" />
          </div>
        )}
        <div className="filterBy">
          Filter By:
          <label htmlFor="fromDate" className="sr-only">From Date</label>
          <input
            id="fromDate"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
          <label htmlFor="toDate" className="sr-only">To Date</label>
          <input
            id="toDate"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
      </div>
      <div className="invoiceTable">
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <CircularProgress />
            <h2>Please wait while we process the request...</h2>
          </div>
        ) : error ? (
          <div className="m-auto text-center px-4 py-4">
            <h1>Error</h1>
            <p>{error}</p>
          </div>
        ) : paginatedLoadingSlips.length > 0 ? (
          <>
            <table className="inTable">
              <thead>
                <tr className="heTable loT">
                  <th>S/N</th>
                  <th>Customer Name</th>
                  <th>Product Name</th>
                  <th className="smm">Unit Price</th>
                  <th>Quantity to Load</th>
                  <th>Truck No</th>
                  <th>Loading No</th>
                  <th>Invoice No</th>
                  <th className="smm">Total Cost</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {paginatedLoadingSlips.map((slip, inx) => {
                  const dateString = slip.createdAt;
                  const isReturned =
                    slip.requisitions[0]?.quantityToLoad === 0 &&
                    slip.requisitions[0]?.quantityLoaded === 0;
                  const formattedDate = new Date(dateString).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  });
                  return (
                    <tr key={slip._id || inx} className="taData">
                      <td>{page * rowsPerPage + inx + 1}</td>
                      <td>
                        {slip.requisitions[0]?.customer?.businessName ?? "N/A"}
                        {isReturned && <span className="returned-badge">Returned</span>}
                      </td>
                      <td>{slip.requisitions[0]?.itemName ?? "N/A"}</td>
                      <td className="smm">{slip.requisitions[0]?.unitPrice ?? 0}</td>
                      <td>{slip.requisitions[0]?.quantityToLoad ?? 0}</td>
                      <td>{slip.truckNumber ?? "N/A"}</td>
                      <td>{slip.loadingNumber ?? "N/A"}</td>
                      <td>{slip.invoice?.invoiceNumber ?? ""}</td>
                      <td className="smm">{slip.requisitions[0]?.totalCost ?? 0}</td>
                      <td>{formattedDate}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="aggregate">
              {Object.entries(customerQuantities).map(([customer, quantity]) => (
                <div key={customer}>
                  <strong>{customer}: </strong>
                  {quantity}
                </div>
              ))}
              <div>
                <strong>Total Quantity to Load: </strong>
                {totalQuantityToLoad}
              </div>
            </div>
          </>
        ) : (
          <div className="m-auto text-center px-4 py-4">
            <h1>No data available.</h1>
            <p>Please check back later or adjust your filters.</p>
          </div>
        )}
      </div>
      <TablePagination
        rowsPerPageOptions={[15, 25, 50]}
        component="div"
        count={filteredLoadingSlips.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <div className="dowBtn">
        <button onClick={generatePDF}>Print Report</button>
        <button onClick={exportToExcel}>Export</button>
      </div>
    </div>
  );
};

export default LoadingReportTable;